import { motion } from "framer-motion";
import type { ReactNode } from "react";
import {
  RiArrowRightLine,
  RiInstagramLine,
  RiTiktokFill,
  RiWhatsappFill,
} from "@remixicon/react";
import { Link } from "react-router-dom";

import ImamStorePreview from "@/assets/hero-imamabubakarbrand.png";
import BookOnTheSpotPreview from "@/assets/hero-bookonthespot.png";
import GrandHillsPreview from "@/assets/hero-grandhills.png";
import SocialFace1 from "@/assets/social-face-1.jpg";
import SocialFace2 from "@/assets/social-face-2.jpg";
import SocialFace3 from "@/assets/social-face-3.jpg";
import SocialFace4 from "@/assets/social-face-4.jpg";
import SocialFace5 from "@/assets/social-face-5.jpg";
import { ColorBand, ColorBandInner } from "@/components/landing/ColorBand";
import { FaqAccordion } from "@/components/landing/FaqAccordion";
import { FooterCta } from "@/components/landing/FooterCta";
import { HeroShowcase } from "@/components/landing/HeroShowcase";
import {
  fadeIn,
  fadeUp,
  inView,
  staggerContainer,
  staggerItem,
} from "@/components/landing/marketing-motion";
import { StorePreviewMock } from "@/components/landing/StorePreviewMock";
import { Button } from "@/components/ui/button";
import { useStartCta } from "@/hooks/useAppSession";
import { cn } from "@/lib/utils";

const analyticsFlow = [
  { label: "Visits", value: "2.4k", hint: "This week" },
  { label: "Product views", value: "1.1k", hint: "Top SKUs" },
  { label: "Orders", value: "186", hint: "Paid" },
  { label: "Revenue", value: "₦840k", hint: "Gross" },
] as const;

const steps = [
  {
    step: "01",
    title: "Claim your link",
    body: "Sign up free, add your business details, and get sqale.shop/yourbrand in minutes.",
  },
  {
    step: "02",
    title: "Add products",
    body: "Upload your products, photos, and prices so customers can see everything without asking in DMs.",
  },
  {
    step: "03",
    title: "Share everywhere",
    body: "Drop your link in bio, stories, WhatsApp status, anywhere you sell.",
  },
  {
    step: "04",
    title: "Grow with insight",
    body: "See what sells, who returns, and when to restock. No more guessing.",
  },
] as const;

const audiences = [
  {
    title: "Fashion & lifestyle",
    body: "Sizes, drops, and restocks without repeating yourself in DMs.",
    img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop&q=80",
    to: "/solutions/fashion-lifestyle",
  },
  {
    title: "Food & drinks",
    body: "Let customers view your menu and order before they message.",
    img: "https://images.unsplash.com/photo-1539755530862-00f623c00f52?w=600&h=400&fit=crop&q=80",
    to: "/solutions/food-beverage",
  },
  {
    title: "Services & bookings",
    body: "Packages and time slots in one link.",
    img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&h=400&fit=crop&q=80",
    to: "/solutions/services-bookings",
  },
] as const;

const socialFaces = [SocialFace1, SocialFace2, SocialFace5] as const;

const productStack = [
  {
    name: "Linen two-piece",
    price: "₦18,500",
    rotate: "-6deg",
    offset: "translate-y-2",
  },
  {
    name: "Ankara midi",
    price: "₦24,000",
    rotate: "3deg",
    offset: "-translate-y-1",
  },
  {
    name: "Court sneakers",
    price: "₦32,000",
    rotate: "-2deg",
    offset: "translate-y-3",
  },
] as const;

const faqs = [
  {
    q: "Is Sqaleshop a marketplace?",
    a: "No. You get your own sales link and customers buy from you directly.",
  },
  {
    q: "I only sell on Instagram and WhatsApp, is this for me?",
    a: "Yes. Sqaleshop is built for social sellers who need a link that actually sells.",
  },
  {
    q: "What is a sales link?",
    a: "A sales link is a link that you can share with your customers to buy your products. It's like a link-in-bio that actually sells.",
  },
  {
    q: "Can I start without paying?",
    a: "Yes. Free includes your sales link, basic products, orders, and Sqaleshop branding. Upgrade to Growth when you need more.",
  },
  {
    q: "How do I get started?",
    a: "Sign up free, add your business details, and get sqale.shop/yourbrand in minutes.",
  },
] as const;

function BandCta({
  href,
  children,
  tone,
}: {
  href: string;
  children: ReactNode;
  tone: "lime" | "lavender" | "primary";
}) {
  const isExternal = href.startsWith("http")
  const className = cn(
    "mt-8 rounded-full px-8 font-semibold shadow-none",
    tone === "lime"
      ? "bg-[#d4f272] text-primary hover:bg-[#c8ea5c]"
      : tone === "primary"
        ? "bg-primary text-white hover:bg-primary/90"
        : "bg-[#be9afc] text-primary hover:bg-[#b089f5]",
  );

  return (
    <Button size="xl" className={className} asChild>
      {isExternal ? (
        <a href={href} className="gap-2">
          {children}
          <RiArrowRightLine className="size-4" />
        </a>
      ) : (
        <Link to={href} className="gap-2">
          {children}
          <RiArrowRightLine className="size-4" />
        </Link>
      )}
    </Button>
  );
}

function PhoneFrame({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[2rem] border-4 border-white/25 bg-card shadow-[0_32px_80px_-24px_rgba(0,0,0,0.45)]",
        className,
      )}
    >
      <img
        src={src}
        alt={alt}
        className="aspect-9/16 w-full object-cover object-top"
      />
    </div>
  );
}

export function HomePage() {
  const start = useStartCta("Get started free")

  return (
    <div>
      <HeroShowcase />

      {/* Create & customize, royal blue, phone + copy */}
      <ColorBand className="bg-primary text-white">
        <div
          className="pointer-events-none absolute -left-24 top-10 size-80 rounded-full bg-white/10 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-16 bottom-0 size-72 rounded-full bg-[#be9afc]/30 blur-3xl"
          aria-hidden
        />
        <ColorBandInner>
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
            <motion.div
              {...fadeIn}
              className="relative flex justify-center lg:justify-start"
            >
              <div
                className="pointer-events-none absolute -left-6 top-8 size-10 rounded-full bg-[#be9afc]"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute right-8 bottom-10 size-7 rounded-full bg-[#be9afc]"
                aria-hidden
              />
              <StorePreviewMock className="border-white/25 shadow-[0_32px_80px_-24px_rgba(0,0,0,0.45)]" />
            </motion.div>

            <motion.div {...fadeUp} className="text-center lg:text-left">
              <h2 className="font-display text-4xl leading-[0.92] sm:text-5xl lg:text-[3.4rem] xl:text-[3.85rem]">
                Create and customize your sales link in minutes
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-base font-semibold leading-relaxed text-white/90 xl:text-lg lg:mx-0">
                Brand name, bio, products, and checkout on one page. No website
                to build, just a link that actually sells.
              </p>
              <div className="flex justify-center lg:justify-start">
                <BandCta href={start.href} tone="primary">
                  {start.label}
                </BandCta>
              </div>
            </motion.div>
          </div>
        </ColorBandInner>
      </ColorBand>

      {/* Share anywhere, burgundy, copy + stacked phones */}
      <ColorBand className="bg-[#be9afc] text-[#2a0e5c]">
        <div
          className="pointer-events-none absolute -right-20 top-0 size-96 rounded-full bg-[#be9afc]/20 blur-3xl"
          aria-hidden
        />
        <ColorBandInner>
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp} className="text-center lg:text-left">
              <h2 className="font-display text-4xl leading-[0.92] sm:text-5xl lg:text-[3.4rem] xl:text-[3.85rem]">
                Share your sales link anywhere you like
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-base font-semibold leading-relaxed text-[#2a0e5c]/80 xl:text-lg lg:mx-0">
                Instagram bio, WhatsApp status, TikTok, stories, just paste your
                link and let customers browse, buy, and come back.
              </p>
              <div className="flex justify-center lg:justify-start">
                <BandCta href="/features" tone="primary">
                  See how it works
                </BandCta>
              </div>
            </motion.div>

            <motion.div
              {...fadeIn}
              className="relative mx-auto h-[380px] w-full max-w-lg sm:h-[440px]"
            >
              <PhoneFrame
                src={ImamStorePreview}
                alt="Fashion storefront preview"
                className="absolute left-0 top-10 w-[46%] -rotate-8"
              />
              <PhoneFrame
                src={BookOnTheSpotPreview}
                alt="Bookings storefront preview"
                className="absolute left-[27%] top-0 z-10 w-[48%]"
              />
              <PhoneFrame
                src={GrandHillsPreview}
                alt="Hospitality storefront preview"
                className="absolute right-0 top-14 w-[46%] rotate-8"
              />
            </motion.div>
          </div>
        </ColorBandInner>
      </ColorBand>

      {/* Analytics, sage, cards + copy */}
      <ColorBand className="bg-[#d2e4c4] text-primary">
        <div
          className="pointer-events-none absolute -left-16 bottom-0 size-72 rounded-full bg-white/50 blur-3xl"
          aria-hidden
        />
        <ColorBandInner>
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={inView}
              className="grid grid-cols-2 gap-4"
            >
              {analyticsFlow.map((item) => (
                <motion.div
                  key={item.label}
                  variants={staggerItem}
                  className="rounded-[1.75rem] bg-white p-5 shadow-[0_16px_40px_-24px_rgba(69,24,146,0.2)] sm:p-6"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary/55">
                    {item.label}
                  </p>
                  <p className="font-display mt-2 text-3xl leading-none sm:text-4xl">
                    {item.value}
                  </p>
                  <div className="mt-4 flex h-10 items-end gap-1">
                    {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                      <span
                        key={i}
                        className="flex-1 rounded-full bg-[#be9afc]/80"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                  <p className="mt-3 text-xs font-medium text-primary/50">
                    {item.hint}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div {...fadeUp} className="text-center lg:text-left">
              <h2 className="font-display text-4xl leading-[0.92] sm:text-5xl lg:text-[3.4rem] xl:text-[3.85rem]">
                Analyze your audience and keep them buying
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-base font-semibold leading-relaxed text-primary/80 xl:text-lg lg:mx-0">
                Track visits, product views, orders, and revenue on Free.
                Upgrade to Growth for deeper insight, customer data, and
                Sqaleshop AI.
              </p>
              <div className="flex justify-center lg:justify-start">
                <BandCta href="/pricing" tone="primary">
                  Compare Free vs Growth
                </BandCta>
              </div>
            </motion.div>
          </div>
        </ColorBandInner>
      </ColorBand>

      {/* Social proof + feature grid */}
      <ColorBand className="bg-white text-primary">
        <ColorBandInner className="text-center">
          <motion.h2
            {...fadeUp}
            className="font-display mx-auto max-w-4xl text-4xl leading-[0.92] sm:text-5xl lg:text-[3.4rem]"
          >
            The sales link built for businesses that started in the DMs
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={inView}
            className="mt-12 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
          >
            {socialFaces.map((src) => (
              <motion.img
                key={src}
                variants={staggerItem}
                src={src}
                alt=""
                className="size-20 rounded-full object-cover shadow-md ring-4 ring-white sm:size-28 lg:size-32"
              />
            ))}
            {[
              { label: "Instagram", Icon: RiInstagramLine },
              { label: "WhatsApp", Icon: RiWhatsappFill },
              { label: "TikTok", Icon: RiTiktokFill },
            ].map(({ label, Icon }) => (
              <motion.div
                key={label}
                variants={staggerItem}
                className="flex size-20 flex-col items-center justify-center rounded-[1.75rem] bg-[#f3f1ec] text-primary sm:size-28 lg:size-32"
              >
                <Icon className="size-7 sm:size-8" />
                <span className="mt-1 text-[10px] font-semibold sm:text-xs">
                  {label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </ColorBandInner>

        <ColorBandInner className="mt-16 lg:mt-20">
          <div className="grid gap-5 lg:grid-cols-3">
            <motion.article
              {...fadeIn}
              className="flex min-h-[420px] flex-col overflow-hidden rounded-[2.5rem] bg-[#d4c4fc] p-6 text-left sm:p-8"
            >
              <h3 className="font-display text-2xl leading-tight text-primary sm:text-3xl">
                Your page, your brand
              </h3>
              <p className="mt-2 text-sm font-semibold text-primary/70">
                A sales link that looks like your business, not a generic store
                template.
              </p>
              <div className="mt-8 flex flex-1 items-end justify-center">
                <div className="w-full max-w-[220px] rounded-[1.75rem] bg-white p-4 shadow-lg">
                  <div className="mx-auto size-12 rounded-full bg-primary/15" />
                  <p className="mt-2 text-center font-heading text-sm text-primary">
                    Your brand
                  </p>
                  <div className="mt-3 space-y-2">
                    {["Shop products", "WhatsApp us", "New arrivals"].map(
                      (label) => (
                        <div
                          key={label}
                          className="rounded-full bg-[#f3f1ec] py-2 text-center text-[11px] font-semibold text-primary"
                        >
                          {label}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </motion.article>

            <motion.article
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.08 }}
              className="flex min-h-[420px] flex-col overflow-hidden rounded-[2.5rem] bg-[#efe04a] p-6 text-left sm:p-8"
            >
              <h3 className="font-display text-2xl leading-tight text-primary sm:text-3xl">
                Products that sell themselves
              </h3>
              <p className="mt-2 text-sm font-semibold text-primary/70">
                Photos, prices, and variants, so customers stop asking in chat.
              </p>
              <div className="relative mt-10 flex flex-1 flex-col justify-center gap-3 px-2">
                {productStack.map((item) => (
                  <div
                    key={item.name}
                    className={cn(
                      "rounded-2xl bg-white px-5 py-4 shadow-[0_12px_32px_-16px_rgba(0,0,0,0.25)]",
                      item.offset,
                    )}
                    style={{ rotate: item.rotate }}
                  >
                    <p className="font-heading text-sm text-primary">
                      {item.name}
                    </p>
                    <p className="text-xs font-semibold text-primary/55">
                      {item.price}
                    </p>
                  </div>
                ))}
              </div>
            </motion.article>

            <motion.article
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.14 }}
              className="flex min-h-[420px] flex-col overflow-hidden rounded-[2.5rem] bg-[#1a1a6e] p-6 text-left text-white sm:p-8"
            >
              <h3 className="font-display text-2xl leading-tight sm:text-3xl">
                Numbers you can act on
              </h3>
              <p className="mt-2 text-sm font-semibold text-white/75">
                Orders, revenue, and repeat buyers, not scattered screenshots.
              </p>
              <div className="mt-8 flex flex-1 flex-col justify-end rounded-[1.75rem] bg-white/10 p-5 ring-1 ring-white/15">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/60">
                  This month
                </p>
                <p className="font-display mt-1 text-4xl">₦840k</p>
                <div className="mt-6 space-y-3">
                  {[
                    { label: "Fashion drop", w: "88%" },
                    { label: "Pre-orders", w: "64%" },
                    { label: "Walk-ins", w: "41%" },
                  ].map((row) => (
                    <div key={row.label}>
                      <div className="mb-1 flex justify-between text-[11px] font-medium text-white/70">
                        <span>{row.label}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-white/15">
                        <div
                          className="h-full rounded-full bg-[#d4f272]"
                          style={{ width: row.w }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.article>
          </div>
        </ColorBandInner>
      </ColorBand>

      {/* How it works + audiences */}
      <ColorBand className="bg-[#f6f4ef] text-primary">
        <ColorBandInner>
          <motion.h2
            {...fadeUp}
            className="font-display max-w-3xl text-4xl leading-[0.92] sm:text-5xl lg:text-[3.25rem]"
          >
            From link to orders in four steps
          </motion.h2>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={inView}
                transition={{ delay: i * 0.06, duration: 0.45 }}
              >
                <span className="font-display text-6xl leading-none text-primary/20">
                  {s.step}
                </span>
                <h3 className="font-heading mt-3 text-xl text-primary">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm font-semibold leading-relaxed text-primary/65">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.h2
            {...fadeUp}
            className="font-display mt-24 max-w-3xl text-4xl leading-[0.92] sm:text-5xl lg:text-[3.25rem]"
          >
            Made for businesses like yours
          </motion.h2>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {audiences.map((a, i) => (
              <motion.article
                key={a.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={inView}
                transition={{ delay: i * 0.08, duration: 0.45 }}
                className="overflow-hidden rounded-[2.5rem] bg-white"
              >
                <img
                  src={a.img}
                  alt=""
                  className="aspect-4/3 w-full object-cover"
                />
                <div className="p-7">
                  <h3 className="font-heading text-2xl text-primary">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm font-semibold leading-relaxed text-primary/65">
                    {a.body}
                  </p>
                  <Link
                    to={a.to}
                    className="text-major mt-5 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    View solution
                    <RiArrowRightLine className="size-4" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </ColorBandInner>
      </ColorBand>

      {/* Testimonial 
      <ColorBand className="bg-white text-primary">
        <ColorBandInner className="max-w-4xl text-center lg:max-w-4xl">
          <motion.img
            {...fadeIn}
            src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1200&h=500&fit=crop&q=80"
            alt=""
            className="mx-auto h-56 w-full rounded-[6rem] object-cover sm:h-72 lg:h-80"
          />
          <motion.blockquote
            {...fadeUp}
            className="font-display mt-10 text-2xl leading-snug sm:text-3xl lg:text-[2.35rem] lg:leading-[1.15]"
          >
            “Sqaleshop simplifies the process for sellers to share everything they
            sell in one inclusive link.”
          </motion.blockquote>
          <p className="mt-5 text-sm font-semibold text-primary/55">Built for social sellers</p>
        </ColorBandInner>
      </ColorBand>
      */}

      {/* FAQ */}
      <ColorBand id="faq" className="bg-primary text-white">
        <ColorBandInner>
          <motion.h2
            {...fadeUp}
            className="font-display max-w-3xl text-4xl leading-[0.92] sm:text-5xl lg:text-[3.4rem] text-center mx-auto"
          >
            Questions? Answered.
          </motion.h2>
          <div className="mt-12 max-w-3xl mx-auto">
            <FaqAccordion items={faqs} variant="onDark" />
          </div>
        </ColorBandInner>
      </ColorBand>

      <FooterCta />
    </div>
  );
}
